import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, ArrowUp, ArrowDown, Trash2, Save, Plus } from "lucide-react";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { DEFAULT_TESTIMONIALS } from "@/components/Testimonials";

const LIST_SECTIONS = [
  { key: "media_hero_full", label: "Homepage Hero — Full-screen background", help: "The large full-screen hero media behind the logo. Upload one photo or video (the first item is shown). Works best with a landscape image or video." },
  { key: "media_mosaic", label: "Homepage — 6-image mosaic (2 × 3)", help: "Exactly 6 photos, shown as a 2-column × 3-row mosaic below the Explore button. Order matters — portrait images look best." },
];
const IMAGE_SECTIONS = [
  { key: "home_cta_image", label: "Homepage CTA Background", help: "Background image behind “Let’s plan something unforgettable.”" },
  { key: "about_image", label: "About Page Image", help: "Main image on the About page." },
  { key: "staff_login_image", label: "Staff Login Image", help: "Image on the staff login screen." },
];

const genId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `m_${Date.now()}_${Math.random().toString(36).slice(2)}`);

// Ensure every item has a stable unique id (mid) used for keys + all actions.
const withIds = (arr) => (Array.isArray(arr) ? arr.map((it) => (it && it.mid ? it : { ...it, mid: genId() })) : []);

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data; // { url, type }
}

function GradedThumb({ item }) {
  const isVideo = item.type === "video";
  const single = assetUrl(item.url || item.mp4);
  const hasSrc = !!(single || item.webm);
  return (
    <div className="relative w-24 h-28 rounded-md overflow-hidden bg-[#0E0E0C] flex-shrink-0 shadow">
      {!hasSrc ? (
        <div className="w-full h-full bg-[#F1EFE8]" />
      ) : isVideo ? (
        item.webm || item.mp4 ? (
          <video className="hero-video-graded w-full h-full object-cover" muted playsInline loop autoPlay preload="metadata">
            {item.webm && <source src={assetUrl(item.webm)} type="video/webm" />}
            {(item.mp4 || item.url) && <source src={assetUrl(item.mp4 || item.url)} type="video/mp4" />}
          </video>
        ) : (
          <video src={single} className="hero-video-graded w-full h-full object-cover" muted playsInline loop autoPlay preload="metadata" />
        )
      ) : (
        <img src={single} alt="" className="hero-video-graded w-full h-full object-cover" />
      )}
      {hasSrc && <span className="hero-left-grade" />}
    </div>
  );
}

function ListSection({ section, items, onCommit, onRestore }) {
  const [busy, setBusy] = useState(false);
  const list = items || [];

  const add = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const d = await uploadFile(file);
      await onCommit([...list, { mid: genId(), type: d.type || "image", url: d.url }]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const move = (mid, dir) => {
    const i = list.findIndex((x) => x.mid === mid);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onCommit(next);
  };

  const remove = (mid) => {
    const item = list.find((x) => x.mid === mid);
    if (!item) return;
    if (!window.confirm("Delete this media item? Your original upload is kept and can be restored.")) return;
    onCommit(list.filter((x) => x.mid !== mid));
  };

  return (
    <div className="bg-white border border-[#E5E5DF] p-5" data-testid={`mm-section-${section.key}`}>
      <h4 className="font-serif-lux text-lg">{section.label}</h4>
      <p className="text-xs text-[#8a8a80] mb-4">{section.help}</p>
      <div className="flex flex-wrap gap-3 mb-4">
        {list.map((item, i) => (
          <div key={item.mid} className="flex flex-col items-center gap-1" data-testid={`mm-item-${section.key}-${item.mid}`}>
            <GradedThumb item={item} />
            <div className="flex items-center gap-1">
              <button onClick={() => move(item.mid, -1)} disabled={i === 0} data-testid={`mm-up-${section.key}-${item.mid}`} className="p-1 text-[#8a8a80] hover:text-[#47562F] disabled:opacity-30" aria-label="Move up"><ArrowUp size={14} /></button>
              <button onClick={() => move(item.mid, 1)} disabled={i === list.length - 1} data-testid={`mm-down-${section.key}-${item.mid}`} className="p-1 text-[#8a8a80] hover:text-[#47562F] disabled:opacity-30" aria-label="Move down"><ArrowDown size={14} /></button>
              <button onClick={() => remove(item.mid)} data-testid={`mm-del-${section.key}-${item.mid}`} className="p-1 text-[#8a8a80] hover:text-red-600" aria-label="Delete"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <span className="text-sm text-[#8a8a80]">This section is empty — nothing shows on the website. Use “Restore default media” to bring back the built-in media.</span>}
      </div>
      <div className="flex items-center gap-3">
        <label className="btn-outline-dark !py-2 !px-4 cursor-pointer text-xs inline-flex items-center" data-testid={`mm-add-${section.key}`}>
          <Upload size={14} className="mr-1" /> {busy ? "Uploading…" : "Add photo / video"}
          <input type="file" accept="image/*,video/*" className="hidden" onChange={add} />
        </label>
        <button type="button" onClick={onRestore} className="text-xs text-[#8a8a80] hover:text-[#47562F] underline" data-testid={`mm-restore-${section.key}`}>Restore default media</button>
      </div>
    </div>
  );
}

function ImageSection({ section, url, onCommit }) {
  const [busy, setBusy] = useState(false);
  const replace = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { const d = await uploadFile(file); await onCommit(d.url); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="bg-white border border-[#E5E5DF] p-5 flex items-center gap-4" data-testid={`mm-image-${section.key}`}>
      <GradedThumb item={{ type: "image", url }} />
      <div className="flex-1">
        <h4 className="font-serif-lux text-lg">{section.label}</h4>
        <p className="text-xs text-[#8a8a80] mb-3">{section.help}</p>
        <label className="btn-outline-dark !py-2 !px-4 cursor-pointer text-xs inline-flex items-center" data-testid={`mm-replace-${section.key}`}>
          <Upload size={14} className="mr-1" /> {busy ? "Uploading…" : "Replace image"}
          <input type="file" accept="image/*" className="hidden" onChange={replace} />
        </label>
      </div>
    </div>
  );
}

export function MediaManager({ content, refresh }) {
  const [draft, setDraft] = useState({});
  const [services, setServices] = useState([]);

  useEffect(() => {
    const c = content || {};
    const next = { ...c };
    // Seed each section with the media the site is ACTUALLY using so the admin sees
    // and can manage current media. Assign stable ids. Nothing persisted until an action.
    const listDefaults = {
      media_hero_full: [],
      media_mosaic: [],
    };
    Object.entries(listDefaults).forEach(([k, def]) => {
      const src = Array.isArray(next[k]) ? next[k] : def;
      next[k] = withIds(src);
    });
    next.testimonials = withIds(
      Array.isArray(next.testimonials) ? next.testimonials : DEFAULT_TESTIMONIALS
    );
    setDraft(next);
  }, [content]);

  useEffect(() => { api.get("/services").then((r) => setServices(r.data || [])).catch(() => {}); }, []);

  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  // Persist a section immediately to the DB and refresh site content.
  const persist = async (key, arr) => {
    setDraft((d) => ({ ...d, [key]: arr }));
    try { await api.put("/content", { [key]: arr }); refresh(); toast.success("Saved & published"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Publish current draft values for the given keys (used for text edits / single images).
  const publish = async (keys) => {
    const patch = {};
    keys.forEach((k) => { patch[k] = draft[k] ?? ""; });
    try { await api.put("/content", patch); refresh(); toast.success("Published to the website"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const t = draft.testimonials || [];
  const commitT = (arr) => persist("testimonials", arr);
  const tMove = (mid, dir) => {
    const i = t.findIndex((x) => x.mid === mid);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= t.length) return;
    const n = [...t];
    [n[i], n[j]] = [n[j], n[i]];
    commitT(n);
  };
  const tField = (mid, k, v) => set("testimonials", t.map((x) => (x.mid === mid ? { ...x, [k]: v } : x)));
  const tPhoto = async (mid, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const d = await uploadFile(file);
      commitT(t.map((x) => (x.mid === mid ? { ...x, photo: d.url } : x)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };
  const tAdd = () => commitT([...t, { mid: genId(), name: "New client", eventType: "Wedding", excerpt: "", quote: "", rating: 5, photo: "" }]);
  const tRemove = (mid) => { if (!window.confirm("Remove this testimonial?")) return; commitT(t.filter((x) => x.mid !== mid)); };

  const svcPhoto = async (id, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const d = await uploadFile(file);
      setServices((prev) => prev.map((x) => (x.id === id ? { ...x, image: d.url } : x)));
      await api.put(`/services/${id}`, { image: d.url });
      toast.success("Service image updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  return (
    <div className="space-y-10" data-testid="media-manager">
      <p className="text-sm text-[#4A4A4A]">Add, replace, reorder and delete media for every section of the website. A subtle warm ivory / sage / gold brand treatment is applied automatically on the site — your original uploads are always kept and restorable. Reordering and deleting save instantly.</p>

      <section>
        <h3 className="font-serif-lux text-2xl mb-3">Homepage Hero &amp; Feature Videos</h3>
        <div className="grid gap-4">
          {LIST_SECTIONS.map((s) => (
            <ListSection key={s.key} section={s} items={draft[s.key]} onCommit={(v) => persist(s.key, v)} onRestore={() => persist(s.key, null)} />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif-lux text-2xl">Single Images</h3>
          <button onClick={() => publish(IMAGE_SECTIONS.map((s) => s.key))} className="btn-gold !py-2 !px-5 text-sm inline-flex items-center" data-testid="mm-publish-images"><Save size={14} className="mr-1.5" /> Publish</button>
        </div>
        <div className="grid gap-4">
          {IMAGE_SECTIONS.map((s) => (
            <ImageSection key={s.key} section={s} url={draft[s.key]} onCommit={(v) => persist(s.key, v)} />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif-lux text-2xl">Testimonials</h3>
          <div className="flex gap-2">
            <button onClick={tAdd} className="btn-outline-dark !py-2 !px-4 text-sm inline-flex items-center" data-testid="mm-testi-add"><Plus size={14} className="mr-1" /> Add</button>
            <button onClick={() => publish(["testimonials"])} className="btn-gold !py-2 !px-5 text-sm inline-flex items-center" data-testid="mm-publish-testimonials"><Save size={14} className="mr-1.5" /> Save Text</button>
          </div>
        </div>
        <div className="grid gap-4">
          {t.map((item) => (
            <div key={item.mid} className="bg-white border border-[#E5E5DF] p-5 flex flex-col md:flex-row gap-4" data-testid={`mm-testi-${item.mid}`}>
              <div className="flex flex-col items-center gap-2">
                <GradedThumb item={{ type: "image", url: item.photo }} />
                <label className="btn-outline-dark !py-1.5 !px-3 cursor-pointer text-xs"><Upload size={12} className="mr-1" /> Photo<input type="file" accept="image/*" className="hidden" onChange={(e) => tPhoto(item.mid, e)} /></label>
              </div>
              <div className="flex-1 grid gap-2">
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-lux !py-1.5 text-sm" placeholder="Client name" value={item.name || ""} onChange={(e) => tField(item.mid, "name", e.target.value)} />
                  <input className="input-lux !py-1.5 text-sm" placeholder="Event type" value={item.eventType || ""} onChange={(e) => tField(item.mid, "eventType", e.target.value)} />
                </div>
                <input className="input-lux !py-1.5 text-sm" placeholder="Short excerpt (shown on the photo)" value={item.excerpt || ""} onChange={(e) => tField(item.mid, "excerpt", e.target.value)} />
                <textarea rows={3} className="input-lux !py-1.5 text-sm resize-none" placeholder="Full testimonial" value={item.quote || ""} onChange={(e) => tField(item.mid, "quote", e.target.value)} />
              </div>
              <div className="flex md:flex-col items-center gap-1">
                <button onClick={() => tMove(item.mid, -1)} data-testid={`mm-testi-up-${item.mid}`} className="p-1 text-[#8a8a80] hover:text-[#47562F]"><ArrowUp size={16} /></button>
                <button onClick={() => tMove(item.mid, 1)} data-testid={`mm-testi-down-${item.mid}`} className="p-1 text-[#8a8a80] hover:text-[#47562F]"><ArrowDown size={16} /></button>
                <button onClick={() => tRemove(item.mid)} data-testid={`mm-testi-del-${item.mid}`} className="p-1 text-[#8a8a80] hover:text-red-600"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {t.length === 0 && <p className="text-sm text-[#8a8a80]">No testimonials yet. Add one to override the built-in defaults.</p>}
        </div>
      </section>

      <section>
        <h3 className="font-serif-lux text-2xl mb-3">Services / Occasions Images</h3>
        <div className="grid gap-4">
          {services.map((s) => (
            <div key={s.id} className="bg-white border border-[#E5E5DF] p-5 flex items-center gap-4" data-testid={`mm-service-${s.id}`}>
              <GradedThumb item={{ type: "image", url: s.image }} />
              <div className="flex-1">
                <h4 className="font-serif-lux text-lg">{s.title}</h4>
                <p className="text-xs text-[#8a8a80] mb-3">Image shown on the Services page for this offering.</p>
                <label className="btn-outline-dark !py-2 !px-4 cursor-pointer text-xs inline-flex items-center"><Upload size={14} className="mr-1" /> Replace image<input type="file" accept="image/*" className="hidden" onChange={(e) => svcPhoto(s.id, e)} /></label>
              </div>
            </div>
          ))}
          <p className="text-xs text-[#8a8a80]">Note: “Occasions” on the homepage are text labels (no images). Service images update instantly on save.</p>
        </div>
      </section>

      <section>
        <h3 className="font-serif-lux text-2xl mb-3">Our Work / Gallery · Venue &amp; Décor</h3>
        <div className="bg-white border border-[#E5E5DF] p-5">
          <p className="text-sm text-[#4A4A4A]">Manage all gallery photos and films — including <strong>Venues</strong> and <strong>Décor</strong> categories — in the dedicated <strong>Gallery</strong> tab, where you can add, edit and delete items with previews.</p>
        </div>
      </section>
    </div>
  );
}
